import { useId } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { springs } from "../motion";
import { TAG_COLORS, type TagColorPickerProps } from "./types";

export function TagColorPicker({
  value,
  onChange,
  label,
}: TagColorPickerProps) {
  const id = useId();

  return (
    <div className="space-y-1.5">
      {label && (
        <label
          htmlFor={`${id}-input`}
          className="text-[11px] font-medium text-muted uppercase tracking-wider"
        >
          {label}
        </label>
      )}
      <input
        type="hidden"
        id={`${id}-input`}
        value={value}
        onChange={() => {}}
      />
      <div
        role="radiogroup"
        aria-labelledby={`${id}-input`}
        className="flex items-center gap-1.5"
      >
        {TAG_COLORS.map((color) => {
          const isSelected = value.toLowerCase() === color.toLowerCase();
          return (
            <motion.button
              key={color}
              type="button"
              onClick={() => onChange(color)}
              whileTap={{ scale: 0.85 }}
              transition={springs.snappy}
              className={`h-7 w-7 rounded-full flex items-center justify-center ${
                isSelected
                  ? "ring-2 ring-offset-1 ring-primary ring-offset-card"
                  : "hover:scale-110 transition-transform"
              }`}
              style={{ backgroundColor: color }}
            >
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={springs.bouncy}
                >
                  <Check
                    className="h-3.5 w-3.5"
                    style={{
                      color: getContrastColor(color),
                    }}
                  />
                </motion.div>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#ffffff";
}
